import type { DirectorySearchResult } from "@/services/directory-search-service"

import { Check, User, Users } from "@/components/icons"
import { cn } from "@/lib/utils"
import { getDirectoryGroupValue } from "@/services/directory-search-service"
import { SettingsService } from "@/services/settings-service"

type DirectorySearchResultItemProps = {
  result: DirectorySearchResult
  isSelected?: boolean
  onClick?: () => void
  /** Override the grouping attribute used for the gray pill. When provided,
   *  uses this instead of the (potentially stale) SettingsService cache. */
  groupAttribute?: string
}

function getBadgeClassName(isUser: boolean, isSelected: boolean): string {
  if (isUser) {
    return isSelected ? "bg-primary/20 text-primary" : "bg-primary/10 text-primary"
  }
  return isSelected
    ? "bg-secondary/80 text-secondary-foreground"
    : "bg-secondary text-secondary-foreground"
}

export function DirectorySearchResultItem({
  result,
  isSelected = false,
  onClick,
  groupAttribute: groupAttributeProp,
}: DirectorySearchResultItemProps) {
  const isUser = result.type === "user"
  const Icon = isUser ? User : Users
  const hasAccentBackground = isSelected

  // Text colors that adapt to accent background
  const primaryTextColor = hasAccentBackground ? "text-accent-foreground" : "text-foreground"
  const secondaryTextColor = hasAccentBackground
    ? "text-accent-foreground/80"
    : "text-muted-foreground"

  // Secondary text content (email or jobTitle)
  const secondaryText = result.email || (!result.email && result.jobTitle ? result.jobTitle : null)

  // Grouping attribute value (department / companyName / officeLocation / city).
  // Only shown for users; groups don't have these attributes.
  // Prefer the prop (live from parent) over the SettingsService cache (stale after config saves).
  const groupAttribute =
    groupAttributeProp ??
    SettingsService.getInstance().settings.local_ai_lead_config?.group_attribute ??
    "department"
  const groupValue = isUser ? getDirectoryGroupValue(result, groupAttribute) : undefined

  return (
    <button
      type="button"
      className={cn(
        "group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors",
        "hover:bg-accent focus:bg-accent focus:outline-none",
        isSelected && "bg-accent"
      )}
      onClick={onClick}
    >
      {/* Icon/Avatar */}
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full",
          isUser
            ? "bg-primary/10 text-primary"
            : "border-foreground bg-secondary text-secondary-foreground border"
        )}
      >
        <Icon className="size-4" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "truncate font-medium transition-colors",
              "group-hover:text-accent-foreground",
              primaryTextColor
            )}
          >
            {result.displayName}
          </span>
          <span
            className={cn(
              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase transition-colors",
              getBadgeClassName(isUser, isSelected)
            )}
          >
            {result.type}
          </span>
          {groupValue && (
            <span
              className="bg-muted text-muted-foreground max-w-[12rem] shrink-0 truncate rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              title={groupValue}
            >
              {groupValue}
            </span>
          )}
        </div>
        {secondaryText && (
          <span
            className={cn(
              "block truncate text-sm transition-colors",
              "group-hover:text-accent-foreground/80",
              secondaryTextColor
            )}
          >
            {secondaryText}
          </span>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && <Check className="text-primary size-4 shrink-0" />}
    </button>
  )
}
